/**
 * Builds free-form ticket text for GenerateTicketAiOverview and GenerateTicketQualityReview
 * from the supervisor ticket detail payload.
 */

import {
  formatAiOverviewThreadSections,
  formatTicketThreadEntries,
  type TicketThreadEntry,
} from "../shared/utils/ticketThreadEntries";

export type { TicketThreadEntry };

export type TicketDetailPayload = {
  ticket?: Record<string, unknown> | null;
  conversations?: TicketThreadEntry[] | null;
  notes?: TicketThreadEntry[] | null;
} | null;

/** Ticket fields and description only. */
function buildTicketHeaderContext(selectedTicket: TicketDetailPayload): string {
  const t = selectedTicket?.ticket;
  if (!t || typeof t !== "object") {
    return "";
  }

  const parts: string[] = [];

  const idStr =
    (t.displayId as string) ||
    (t.ticketNumber as string) ||
    (t.id != null ? String(t.id) : "");
  if (idStr) {
    parts.push(`Ticket: ${idStr}`);
  }

  const subject = (t.subject as string) || (t.title as string);
  if (subject && String(subject).trim()) {
    parts.push(`Subject: ${String(subject).trim()}`);
  }

  if (t.status != null && String(t.status).trim()) {
    parts.push(`Status: ${String(t.status).trim()}`);
  }
  if (t.priority != null && String(t.priority).trim()) {
    parts.push(`Priority: ${String(t.priority).trim()}`);
  }

  const company =
    (t.client as { name?: string } | undefined)?.name ||
    (t.company as { name?: string } | undefined)?.name;
  if (company && String(company).trim()) {
    parts.push(`Company: ${String(company).trim()}`);
  }

  const req = t.requester as { name?: string; email?: string } | undefined;
  if (req && (req.name || req.email)) {
    const rq = [req.name, req.email].filter(Boolean).join(" — ");
    if (rq) {
      parts.push(`Requester: ${rq}`);
    }
  }

  const assigned = t.assigned_to as { name?: string } | undefined;
  if (assigned?.name && String(assigned.name).trim()) {
    parts.push(`Assigned: ${String(assigned.name).trim()}`);
  }

  if (t.queue != null && String(t.queue).trim()) {
    parts.push(`Queue: ${String(t.queue).trim()}`);
  }

  const ticketType = t.ticketType ?? t.ticket_type;
  if (ticketType != null && String(ticketType).trim()) {
    parts.push(`Type: ${String(ticketType).trim()}`);
  }
  const category = t.category ?? t.ticketCategory ?? t.ticket_category;
  if (category != null && String(category).trim()) {
    parts.push(`Category: ${String(category).trim()}`);
  }

  const desc = t.description;
  if (desc != null && String(desc).trim()) {
    const d = String(desc).trim();
    parts.push("");
    parts.push("Description:");
    parts.push(d.length > 12000 ? `${d.slice(0, 12000)}\n[...truncated...]` : d);
  }

  return parts.join("\n").trim();
}

/**
 * AI Overview ticket context: ticket fields, description, customer conversation, and notes.
 * The active Theo thread is not included here; Horizon passes its id and Cortex loads it.
 */
export function buildTicketAiOverviewContext(selectedTicket: TicketDetailPayload): string {
  const header = buildTicketHeaderContext(selectedTicket);
  if (!header) {
    return "";
  }
  const sections = formatAiOverviewThreadSections(selectedTicket?.conversations, selectedTicket?.notes);
  return sections ? `${header}\n\n${sections}` : header;
}

export type TicketQualityReviewPayload = {
  ticket?: Record<string, unknown> | null;
  conversations?: TicketThreadEntry[] | null;
  notes?: TicketThreadEntry[] | null;
} | null;

const QA_ENTRY_LIMITS = { maxEntries: 60, maxEntryChars: 6000 };

/**
 * Builds the full ticket thread for GenerateTicketQualityReview: header fields
 * plus customer conversation and internal/agent notes with author + timestamp.
 * Time-entry detail travels inside notes content for PSA threads; resolution
 * notes (Description / Action Taken / Root Cause) are included verbatim.
 */
export function buildTicketQualityReviewContext(selectedTicket: TicketQualityReviewPayload): string {
  const header = buildTicketHeaderContext(selectedTicket);
  const parts: string[] = [header];
  parts.push("");
  parts.push(...formatTicketThreadEntries("Customer conversation", selectedTicket?.conversations, QA_ENTRY_LIMITS));
  parts.push("");
  parts.push(
    ...formatTicketThreadEntries("Notes (internal, agent, and customer-facing)", selectedTicket?.notes, QA_ENTRY_LIMITS)
  );
  return parts.join("\n").trim();
}
